import { useMemo, useState } from 'react';
import { Turnstile } from './Turnstile';
import type { GroupId, Statement } from '../types';
import { pct } from '../lib/format';

interface StatementCardProps {
  statement: Statement;
  groupId: GroupId;
  groupLabel: string;
  otherLabel: string;
  siteKey?: string;
  token?: string;
  onToken: (token?: string) => void;
  onVote: (agreement: number, confidence: number, guessOther: number, startedAt: number) => Promise<void>;
  onCompose: () => void;
  busy: boolean;
}

export function StatementCard({ statement, groupLabel, otherLabel, siteKey, token, onToken, onVote, onCompose, busy }: StatementCardProps) {
  const [agreement, setAgreement] = useState(1);
  const [confidence, setConfidence] = useState(0.72);
  const [guessOther, setGuessOther] = useState(0.38);
  const startedAt = useMemo(() => Date.now(), [statement.id]);

  return (
    <section className="statementCard card">
      <div className="statementMeta">
        <span className={`status ${statement.status}`}>{statement.status}</span>
        <span>{statement.nA + statement.nB} human signals</span>
      </div>
      <blockquote>{statement.text}</blockquote>
      <div className="sliders">
        <label>
          <span>Your agreement</span>
          <input
            type="range"
            min="-2"
            max="2"
            step="1"
            value={agreement}
            onChange={(event) => setAgreement(Number(event.target.value))}
          />
          <b>{agreementLabel(agreement)}</b>
        </label>
        <label>
          <span>Confidence</span>
          <input type="range" min="0" max="1" step="0.01" value={confidence} onChange={(event) => setConfidence(Number(event.target.value))} />
          <b>{pct(confidence)}</b>
        </label>
        <label>
          <span>What percent of “{otherLabel}” will agree?</span>
          <input type="range" min="0" max="1" step="0.01" value={guessOther} onChange={(event) => setGuessOther(Number(event.target.value))} />
          <b>{pct(guessOther)}</b>
        </label>
      </div>
      <div className="humanRow">
        <Turnstile siteKey={siteKey} onToken={onToken} />
        <p>Consent: I am human, anonymous, and my aggregated answers may help build a public commons map.</p>
      </div>
      <div className="actions">
        <button className="ghost" onClick={onCompose}>Suggest a better bridge</button>
        <button className="primary" disabled={busy || !token} onClick={() => onVote(agreement, confidence, guessOther, startedAt)}>
          {busy ? 'Scoring…' : `Lock answer as ${groupLabel}`}
        </button>
      </div>
    </section>
  );
}

function agreementLabel(value: number) {
  if (value <= -2) return 'Strong no';
  if (value === -1) return 'Lean no';
  if (value === 0) return 'Mixed';
  if (value === 1) return 'Lean yes';
  return 'Strong yes';
}
